import { useCallback } from 'react';

import {
    NavigationProp,
    NavigatorScreenParams,
    ParamListBase,
    Route,
    StackActions,
    createNavigationContainerRef,
    useNavigation,
} from '@react-navigation/native';

import { URLS } from '@/data';
import { navigateToAccountSuspendedModal } from '@/feature/authentication/utils';
import { useLinkingStore } from '@/feature/entry-share/hooks/use-linking';
import { getIsOnlyTrustlyKycEnabled } from '@/feature/kyc/components/utils';
import { KycProvider, getInitialKycScreens, kycRouteNames } from '@/feature/kyc/const';
import { useActiveWalletStore } from '@/hooks/use-active-wallet';
import { getAuthUserConfig } from '@/hooks/use-auth-user-config';
import { applyUserDefaultProduct } from '@/hooks/use-auth-user-settings';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { isDobFirstAB } from '@/utils/ab-testing';
import { getProductLobby, getProductRoot } from '@/utils/product';

import {
    AccountStackParamList,
    DepositStackParamList,
    IdpvSource,
    KycStackParamList,
    RootStackParamList,
    WithdrawStackParamList,
} from './types';

type HomeScreen = 'lobby' | 'account';

export const navigationRef = createNavigationContainerRef<RootStackParamList>();

/**
 * Navigates to the Home tabs of the active Product (Pickem/SBK)
 * @param navigation the root navigation object
 * @param screen the tab that should be pre-selected
 */
export function navigateHome(
    navigation: Omit<NavigationProp<ParamListBase>, 'getState'>,
    screen: HomeScreen = 'lobby'
) {
    const selectedProduct = useJurisdictionStore.getState().product;
    navigateToProduct(navigation, selectedProduct, screen);
}

// the root route is reused, so its tab keeps whatever screen the user left it on.
// `pop` sends that tab's stack back; without it v7 pushes a second copy instead of going back.
const getHomeScreenParams = (screen: HomeScreen, product?: Product) => {
    if (screen === 'account') {
        return { screen: 'AccountStack', params: { screen: 'Account', pop: true } };
    }

    // the lobby tab is named per product, unlike AccountStack which is shared
    const lobby = getProductLobby(product);
    return { screen: lobby.tab, params: { screen: lobby.screen, pop: true } };
};

/**
 * Like {@link navigateHome}, but pops to the existing root screen instead of building a fresh one,
 * so it's one animation instead of two. Falls back to {@link navigateHome} when the root was never
 * pushed (e.g. KYC straight after signup), since there's nothing to pop to.
 */
export function popToHome(navigation: NavigationProp<ParamListBase>, screen: HomeScreen = 'lobby') {
    const product = useJurisdictionStore.getState().product;
    const root = getProductRoot(product);
    if (!isRouteInHistory(navigation, root)) {
        navigateHome(navigation, screen);
        return;
    }

    useActiveWalletStore.getState().resetActiveWallet();

    navigation.dispatch(StackActions.popTo(root, getHomeScreenParams(screen, product)));
}

/**
 * Whether the screen is still in the navigator's history, so it can be popped back to
 */
export function isRouteInHistory(navigation: NavigationProp<ParamListBase>, routeName: keyof RootStackParamList) {
    return navigation.getState()?.routes.some(route => route.name === routeName) ?? false;
}

const PRODUCT_BETSLIP_STACKS = {
    FantasyPickSlipStack: { screen: 'FantasyPickSlip', params: {} },
    BetSlipStack: { screen: 'BetSlipModal' },
} as const;

type ProductBetSlipStack = keyof typeof PRODUCT_BETSLIP_STACKS;

/**
 * The bet slip the deposit flow was opened from, if it is still in the root stack
 */
export function findProductBetSlip(navigation: NavigationProp<ParamListBase>) {
    return (Object.keys(PRODUCT_BETSLIP_STACKS) as ProductBetSlipStack[]).find(stack =>
        isRouteInHistory(navigation, stack)
    );
}

/**
 * Removes every screen above the bet slip
 */
export function popToProductBetSlip(navigation: NavigationProp<ParamListBase>, stack: ProductBetSlipStack) {
    navigation.dispatch(StackActions.popTo(stack, PRODUCT_BETSLIP_STACKS[stack]));
}

/**
 * Similar to {@link navigateHome}, but also takes a product as parameter, when we want to explicitly
 * specify to which product it should navigate.
 * Useful to call this from "product switchers".
 *
 * @param navigation the root navigation object
 * @param product the product(Pickem/SBK) to navigate to
 * @param screen the tab that should be pre-selected
 */
export function navigateToProduct(
    navigation: Omit<NavigationProp<ParamListBase>, 'getState'>,
    product?: Product,
    screen: HomeScreen = 'lobby'
) {
    // reset active wallet whenever switching products, it will re-initialized when navigating to account screen
    useActiveWalletStore.getState().resetActiveWallet();
    const root = getProductRoot(product);
    const screenParams = screen === 'lobby' ? { screen: getProductLobby(product).tab } : { screen: 'AccountStack' };

    navigation.reset({
        index: 0,
        routes: [
            {
                name: root,
                params: screenParams,
            },
        ],
    });
}

type NavigationWithoutState = Omit<NavigationProp<ParamListBase>, 'getState'>;

const getRootRoutes = (navigation: NavigationWithoutState) => navigation.getParent()?.getState()?.routes ?? [];

export function navigateToKyc(navigation: NavigationWithoutState, route: NavigatorScreenParams<KycStackParamList>) {
    // params `pop`: go back to the screen if it's already in the KYC stack
    // options `pop`: go back to the KYC stack if it's already open (e.g. under deposit)
    navigation.navigate('KycStack', { ...route, pop: true }, { pop: true });
}

/**
 * Sends a logged out user from the KYC stack to Login
 */
export function exitKycToLogin(navigation: NavigationWithoutState) {
    const loginRoute = getRootRoutes(navigation).find(({ name }) => name === 'Login') ?? { name: 'Login' };
    const routes: Omit<Route<string>, 'key'>[] = [loginRoute];

    navigation.reset({ index: 0, routes });
}

type FinancialStackRoute =
    | { name: 'WithdrawStack'; params: NavigatorScreenParams<WithdrawStackParamList> }
    | { name: 'DepositStack'; params: NavigatorScreenParams<DepositStackParamList> };

/**
 * Leaves the KYC stack and opens a financial flow. Only call it from inside the KYC flow.
 */
export function kycProceedToFinancialStack(navigation: NavigationWithoutState, route: FinancialStackRoute) {
    const routes = getRootRoutes(navigation);

    if (routes.some(({ name }) => name === route.name)) {
        navigation.navigate(route.name, { ...route.params, pop: true }, { pop: true });
        return;
    }

    const kycStackIndex = routes.findIndex(({ name }) => name === 'KycStack');
    const nextRoutes: Omit<Route<string>, 'key'>[] = [...routes.slice(0, kycStackIndex), route];

    navigation.reset({ index: nextRoutes.length - 1, routes: nextRoutes });
}

/**
 * Determines navigation based on the user's failed KYC count:
 * - If `failedKycCount` < (total initial KYC screens - 1) -> navigate to next KYC screen
 * - Otherwise -> navigate to account suspended modal
 */
export const navigateBasedOnKYCCount = (navigation: NavigationWithoutState, failedKycCount: number) => {
    const isDobFirst = isDobFirstAB();
    const initialKycScreens = getInitialKycScreens({ isDobFirst });
    if (failedKycCount < initialKycScreens.length) {
        navigateToKyc(navigation, { screen: initialKycScreens[failedKycCount] });
    } else {
        navigateToAccountSuspendedModal(navigation);
    }
};

/**
 * Navigates to the KYC onboarding screen based on feature flags.
 * - If only Trustly KYC is enabled -> navigates to KycAcknowledgements with Trustly provider
 * - Otherwise -> navigates to KycSelector
 */
export function navigateToKycSelector(navigation: NavigationWithoutState) {
    if (getIsOnlyTrustlyKycEnabled()) {
        navigateToKyc(navigation, {
            screen: kycRouteNames.ACKNOWLEDGEMENTS,
            params: { provider: KycProvider.TRUSTLY },
        });
        return;
    }

    navigateToKyc(navigation, { screen: 'KycSelector' });
}

/**
 * Navigates the user to the appropriate screen based on their KYC status after logging in.
 * @param navigation the root navigation object
 * @param userEmail - Optional email to avoid an extra API call if already available
 */
export async function handleKYCNavigation(navigation: NavigationWithoutState, userEmail?: string) {
    const userConfig = await getAuthUserConfig();
    const { USER_KYC, FORCE_IDPV, FAILED_KYC_COUNT = 0, TRUSTLY_COMPLETED } = userConfig.data.config;
    const jurisdictionSettings = useJurisdictionStore.getState().jurisdictionSettings;

    const { allProductsHidden } = await applyUserDefaultProduct(userEmail);

    if (allProductsHidden) {
        const currentProduct = useJurisdictionStore.getState().product;
        useLinkingStore.getState().setUnavailableProductRedirect(currentProduct);
    }

    if (USER_KYC) {
        navigateHome(navigation);
    } else if (FORCE_IDPV) {
        navigateToKyc(navigation, { screen: kycRouteNames.SCAN_ID, params: { source: IdpvSource.KYC } });
    } else {
        const isTrustlyKycEnabled =
            jurisdictionSettings?.productConfig?.settings?.enable_trustly_kyc && !TRUSTLY_COMPLETED;

        if (isTrustlyKycEnabled) {
            navigateToKycSelector(navigation);
        } else {
            navigateBasedOnKYCCount(navigation, FAILED_KYC_COUNT);
        }
    }
}

/**
 * Resets the navigation to a specific screen inside the Account stack.
 * Like {@link popToHome}, it pops to the existing root instead of pushing a second one, so
 * callers inside a modal stack (e.g. WithdrawStack) dismiss it rather than leaving it mounted
 * underneath. Falls back to {@link NavigationProp.navigate} when the root was never pushed.
 */
export function resetToAccountScreen<
    Screen extends keyof Pick<
        AccountStackParamList,
        | 'TransactionHistory'
        | 'Timeout'
        | 'ResponsibleGaming'
        | 'ManageDebitCards'
        | 'ManageExpiredDebitCardDetail'
        | 'AccountDetails'
    >
>(
    navigation: NavigationProp<ParamListBase>,
    ...[screen, params]: undefined extends AccountStackParamList[Screen]
        ? [screen: Screen, params?: AccountStackParamList[Screen]]
        : [screen: Screen, params: AccountStackParamList[Screen]]
) {
    const selectedProduct = useJurisdictionStore.getState().product;
    const root = getProductRoot(selectedProduct);
    const screenParams = { screen: 'AccountStack' as const, params: { screen, params, initial: false, pop: true } };

    if (!isRouteInHistory(navigation, root)) {
        navigation.navigate(root, screenParams);
        return;
    }

    navigation.dispatch(StackActions.popTo(root, screenParams));
}

export function useCoreNavigation() {
    const navigation = useNavigation();
    const clearGpsData = useJurisdictionStore(state => state.actions.clearGpsData);

    const openLaunch = useCallback(() => {
        navigation.reset({ routes: [{ name: 'Launch' }] });
    }, [navigation]);

    const openHome = useCallback(
        (screen: HomeScreen = 'lobby') => {
            navigateHome(navigation, screen);
        },
        [navigation]
    );

    const showIneligibleCountry = useCallback(() => {
        clearGpsData();
        navigation.navigate('IneligibleLocationWeb');
    }, [clearGpsData, navigation]);

    const showIneligibleStateError = useCallback(() => {
        clearGpsData();
        navigation.navigate('IneligibleStateModal', {
            title: 'Ineligible location',
            subtitle:
                'It looks like you are in a state that does not allow game play for our products. If you think this is a mistake make sure to turn off VPNs and enable your phone location so we can accurately detect your state.',
            primaryButton: 'See eligible states',
            handlePress: () => {
                navigation.navigate('ModalWebView', { uri: URLS.ALLOWED_STATES });
            },
        });
    }, [clearGpsData, navigation]);

    return { openLaunch, openHome, showIneligibleStateError, showIneligibleCountry };
}

/**
 * use this to navigate without the navigation prop.
 * ! **If you have access to the navigation prop, do not use this.**
 * @see {@link https://reactnavigation.org/docs/navigating-without-navigation-prop/}
 * @param {unknown} name - The name of the route to navigate to.
 * @param {unknown} params - The params to pass to the route.
 */
export function navigate(name: unknown, params?: unknown) {
    if (navigationRef.isReady()) {
        // @ts-expect-error
        navigationRef.navigate(name as never, params as never);
    }
}

/**
 * This function is used to go back in a navigation stack, if it's possible to go back.
 * ! **If you have access to the navigation prop, use navigation.goBack() instead.**
 * If the navigation stack can't go back, nothing happens.
 * The navigationRef variable is a React ref that references a navigation object.
 * The navigationRef variable is set in the App component.
 */
export function goBack() {
    if (navigationRef.isReady() && navigationRef.canGoBack()) {
        navigationRef.goBack();
    }
}

export const useRafNavigation = () => {
    const navigation = useNavigation();

    const goToRaf = useCallback(() => {
        navigation.navigate('InviteFriends');
    }, [navigation]);

    return { goToRaf };
};
